import { DateFormat, toFormattedDateOrNull } from '@/utils/date/format';
import dayjs from 'dayjs';
import { MIN_SELECTABLE_DATE, ValidationError } from './dateRangeUtils';

const VALID_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const VALID_MONTHS_LOWER = VALID_MONTHS.map(m => m.toLowerCase());

const isValidYear = (yearStr: string): boolean => {
  if (!/^\d+$/.test(yearStr)) {
    return false;
  }
  if (yearStr.length < 4) {
    return true; // Still typing
  }
  const yearNum = parseInt(yearStr, 10);
  const currentYear = new Date().getFullYear();
  return yearNum >= 1900 && yearNum <= currentYear + 100;
};

const parseDateFromParts = (day: string, month: string, year: string): dayjs.Dayjs | null => {
  const monthIndex = VALID_MONTHS_LOWER.indexOf(month.toLowerCase());
  if (monthIndex === -1) {
    return null;
  }
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);
  if (isNaN(dayNum) || isNaN(yearNum)) {
    return null;
  }
  const date = dayjs().year(yearNum).month(monthIndex).date(dayNum);
  return date.isValid() ? date : null;
};

const validateDayPart = (part: string): ValidationError | null => {
  // Check if part contains any non-digit characters
  if (!/^\d*$/.test(part)) {
    return { message: 'components.dateRangeInput.validation.invalidDay' };
  }

  // If more than 2 digits, it's invalid
  if (part.length > 2) {
    return { message: 'components.dateRangeInput.validation.invalidDay' };
  }

  // If only 1 digit, user is still typing - allow "0" as they may type "01", "02", etc.
  if (part.length === 1) {
    return null;
  }

  // If exactly 2 digits, validate the range (01-31)
  if (part.length === 2) {
    const dayNum = parseInt(part, 10);
    if (dayNum > 31 || dayNum < 1) {
      return { message: 'components.dateRangeInput.validation.invalidDay' };
    }
  }

  return null;
};

const validateMonthPart = (part: string): ValidationError | null => {
  // Check if it's a text-based month abbreviation (letters only)
  if (/^[A-Za-z]+$/.test(part)) {
    // If 3+ characters, validate as month abbreviation
    if (part.length >= 3) {
      const normalized = part.slice(0, 3).toLowerCase();
      if (!VALID_MONTHS_LOWER.includes(normalized)) {
        return { message: 'components.dateRangeInput.validation.invalidMonth' };
      }
    }
    return null;
  }
  // Handle numeric month input
  if (/^\d+$/.test(part)) {
    // If more than 2 digits, it's invalid (can't be a valid month like 01-12)
    if (part.length > 2) {
      return { message: 'components.dateRangeInput.validation.invalidMonth' };
    }
    // If exactly 2 digits, check if it's a valid month (01-12)
    if (part.length === 2) {
      const monthNum = parseInt(part, 10);
      if (monthNum < 1 || monthNum > 12) {
        return { message: 'components.dateRangeInput.validation.invalidMonth' };
      }
    }
    // 1 digit - user is still typing, no error yet
    return null;
  }
  // Mixed content (letters and digits) is invalid
  if (part.length >= 1) {
    return { message: 'components.dateRangeInput.validation.invalidMonth' };
  }
  return null;
};

const validateYearPart = (part: string): ValidationError | null => {
  // Check if part contains any non-digit characters
  if (!/^\d*$/.test(part)) {
    return { message: 'components.dateRangeInput.validation.invalidYear' };
  }

  // If it's a complete year (4+ digits), validate the range
  if (part.length >= 4) {
    if (!isValidYear(part)) {
      return { message: 'components.dateRangeInput.validation.invalidYear' };
    }
  }

  return null;
};

const validateCompleteDate = (day: string, month: string, year: string): ValidationError | null => {
  if (!/^\d{1,2}$/.test(day) || !/^[A-Za-z]{3}$/.test(month) || !/^\d{4}$/.test(year)) {
    return null;
  }
  const date = parseDateFromParts(day, month, year);
  if (!date) {
    return null;
  }
  const today = dayjs();
  if (date.isAfter(today, 'day')) {
    return { message: 'components.dateRangeInput.validation.futureDate' };
  }
  return null;
};

const validateDateMinSelectable = (day: string, month: string, year: string): ValidationError | null => {
  if (!/^\d{1,2}$/.test(day) || !/^[A-Za-z]{3}$/.test(month) || !/^\d{4}$/.test(year)) {
    return null;
  }
  const date = parseDateFromParts(day, month, year);
  if (!date) {
    return null;
  }
  if (date.isBefore(MIN_SELECTABLE_DATE, 'day')) {
    return {
      message: 'components.dateRangeInput.validation.minDate',
      params: { minDate: toFormattedDateOrNull(MIN_SELECTABLE_DATE, DateFormat.dateFormat, '') },
    };
  }
  return null;
};

/**
 * Validates date input in progress (inline validation while typing).
 * Returns validation error if invalid day (>31), invalid month abbreviation,
 * invalid year, date before MIN_SELECTABLE_DATE, or future date is detected.
 */
export const validateInputInProgress = (input: string): ValidationError => {
  if (!input || input.trim() === '') {
    return {};
  }

  const parts = input.split(' - ');
  const dateParts = parts.flatMap(part => part.trim().split(/\s+/));

  for (let i = 0; i < dateParts.length; i++) {
    const part = dateParts[i];
    const isDay = i === 0 || i === 3;
    const isMonth = i === 1 || i === 4;
    const isYear = i === 2 || i === 5;

    if (isDay) {
      const error = validateDayPart(part);
      if (error) return error;
    }
    if (isMonth) {
      const error = validateMonthPart(part);
      if (error) return error;
    }
    if (isYear) {
      const error = validateYearPart(part);
      if (error) return error;
    }
  }

  // Validate first complete date (day, month, year all present) - only future date check
  if (dateParts.length >= 3 && dateParts[2]?.length === 4) {
    const error = validateCompleteDate(dateParts[0], dateParts[1], dateParts[2]);
    if (error) return error;
  }

  // Validate second complete date (after separator) - only future date check
  if (dateParts.length >= 6 && dateParts[5]?.length === 4) {
    const error = validateCompleteDate(dateParts[3], dateParts[4], dateParts[5]);
    if (error) return error;

    // Validate that end date is not before start date (check this BEFORE MIN_SELECTABLE_DATE)
    const startDate = parseDateFromParts(dateParts[0], dateParts[1], dateParts[2]);
    const endDate = parseDateFromParts(dateParts[3], dateParts[4], dateParts[5]);
    if (startDate && endDate && endDate.isBefore(startDate, 'day')) {
      return { message: 'components.dateRangeInput.validation.endBeforeStart' };
    }

    // Now check MIN_SELECTABLE_DATE for both dates
    const minDateErrorStart = validateDateMinSelectable(dateParts[0], dateParts[1], dateParts[2]);
    if (minDateErrorStart) return minDateErrorStart;

    const minDateErrorEnd = validateDateMinSelectable(dateParts[3], dateParts[4], dateParts[5]);
    if (minDateErrorEnd) return minDateErrorEnd;
  }

  // Check MIN_SELECTABLE_DATE for first date only (when second date is not complete yet)
  if (dateParts.length >= 3 && dateParts[2]?.length === 4 && dateParts.length < 6) {
    const minDateError = validateDateMinSelectable(dateParts[0], dateParts[1], dateParts[2]);
    if (minDateError) return minDateError;
  }

  return {};
};

/**
 * Checks if the input represents a complete date range (DD MMM YYYY - DD MMM YYYY).
 * Returns true if both dates are fully typed with 4-digit years.
 */
export const isCompleteDateRange = (input: string): boolean => {
  if (!input || input.trim() === '') {
    return false;
  }

  const parts = input.split(' - ');
  if (parts.length !== 2) {
    return false;
  }

  const firstDateParts = parts[0].trim().split(/\s+/);
  const secondDateParts = parts[1].trim().split(/\s+/);

  // Check if both dates have day, month, and year (with 4-digit year)
  if (firstDateParts.length !== 3 || secondDateParts.length !== 3) {
    return false;
  }

  const [day1, month1, year1] = firstDateParts;
  const [day2, month2, year2] = secondDateParts;

  const isValidFormat =
    /^\d{1,2}$/.test(day1) &&
    /^[A-Za-z]{3}$/.test(month1) &&
    /^\d{4}$/.test(year1) &&
    /^\d{1,2}$/.test(day2) &&
    /^[A-Za-z]{3}$/.test(month2) &&
    /^\d{4}$/.test(year2);

  return isValidFormat;
};
